import moment from 'moment-timezone';

const pad2 = (value) => String(value).padStart(2, '0');

/** Read the date picker's civil date without converting its browser-local instant. */
export const toCivilDateKey = (value) => {
  const pickerYear = Number(value?.year);
  const pickerMonth = Number(value?.month?.number ?? value?.month);
  const pickerDay = Number(value?.day);
  if (Number.isInteger(pickerYear)
    && Number.isInteger(pickerMonth)
    && Number.isInteger(pickerDay)) {
    return `${pickerYear}-${pad2(pickerMonth)}-${pad2(pickerDay)}`;
  }

  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }

  if (typeof value === 'string') {
    const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})(?:$|[T\s])/);
    if (dateOnly) return dateOnly[1];
  }

  if (typeof value?.format === 'function') {
    const formatted = value.format('YYYY-MM-DD');
    if (/^\d{4}-\d{2}-\d{2}$/.test(formatted)) return formatted;
  }

  return null;
};

/**
 * Convert an inclusive agency-local date selection into an explicit UTC,
 * half-open interval: [start of first day, start of day after last day).
 */
export const buildAgencyUtcDayRange = (startValue, endValue, timezone) => {
  const startDay = toCivilDateKey(startValue);
  const endDay = toCivilDateKey(endValue);
  if (!startDay || !endDay
    || typeof timezone !== 'string'
    || !moment.tz.zone(timezone)) return null;

  const start = moment.tz(startDay, 'YYYY-MM-DD', true, timezone).startOf('day');
  const endExclusive = moment.tz(endDay, 'YYYY-MM-DD', true, timezone)
    .add(1, 'day')
    .startOf('day');
  if (!start.isValid() || !endExclusive.isValid() || endExclusive.isSameOrBefore(start)) return null;

  return {
    fromTime: start.utc().toISOString(),
    toTime: endExclusive.utc().toISOString(),
  };
};

/**
 * Build API boundaries. Legacy/stale sessions without an agency timezone keep
 * civil, offsetless boundaries so the API can resolve them using live agency data.
 */
export const buildAgencyRequestDayRange = (startValue, endValue, timezone) => {
  const utcRange = buildAgencyUtcDayRange(startValue, endValue, timezone);
  if (utcRange) return utcRange;

  const startDay = toCivilDateKey(startValue);
  const endDay = toCivilDateKey(endValue);
  if (!startDay || !endDay) return null;
  const startCivil = moment.utc(startDay, 'YYYY-MM-DD', true);
  const endExclusiveDay = moment.utc(endDay, 'YYYY-MM-DD', true).add(1, 'day');
  if (!startCivil.isValid()
    || !endExclusiveDay.isValid()
    || endExclusiveDay.isSameOrBefore(startCivil)) return null;

  return {
    fromTime: `${startDay}T00:00:00`,
    toTime: `${endExclusiveDay.format('YYYY-MM-DD')}T00:00:00`,
  };
};

/** Read the authoritative IANA timezone returned by a fuel-management payload. */
export const resolveFuelResponseTimezone = (payloads) => {
  const candidates = Array.isArray(payloads) ? payloads : [payloads];
  for (let index = 0; index < candidates.length; index += 1) {
    const value = candidates[index]?.timezone;
    if (typeof value !== 'string') continue;
    const timezone = value.trim();
    if (timezone && moment.tz.zone(timezone)) return timezone;
  }
  return null;
};
